/**
 * Bei der Anwendung von JavaScripts Array.sort() müssen einige Dinge beachtet werden:
 *
 * 1) Ohne Vergleichsfunktion werden die Elemente in Strings umgewandelt und als Strings verglichen behandelt.
 *    Dadurch wird z.B. 12 vor 2 sortiert, weil '12' < '2'; null wird als 'null' einsortiert; ...
 * 2) undefined Werte im Array werden ans Ende sortiert, ohne dafür jemals die Vergleichsfunktion aufzurufen.
 * 3) Strings werden nach ihrem Unicode-Wert sortiert, angebrachter wäre eine alphabetische Sortierung mittels String.localeCompare().
 *    Sonst werden z.B. Umlaute nach den anderen Buchstaben einsortiert und Großbuchstaben vor Kleinbuchstaben.
 * 4) Problematische Werte (undefined, null, NaN, empty/blank Strings, Infinity, -Infinity, Invalid Date, komplexe Objekte, gemischte Arrays, ...) sollten bewusst behandelt werden
 *
 *  Array.sort() sollte daher im Regelfall nicht ohne Vergleichsfunktion verwendet werden und die explizite Vergleichsfunktion zu einem klar definiertem Verhalten führen.
 *
 * Die folgenden Funktionen sollen dabei helfen, häufig vorkommende Anwendungsfälle der Sortierung abzudecken und die Vergleichsfunktionen kurz, prägnant und klar definiert zu
 * halten und einen bewussten Umgang mit undefined und null Werten zu fördern.
 *
 * Beispiel:
 *
 * data.sort(withProp('name', nilLast(order(dir ?? 1, byLocale))));
 *
 */

import { isInvalidDate } from './date-utils';

export type DefinedValue = NonNullable<unknown>;
export type NonUndefinedValue = DefinedValue | null; // alle Typen außer undefined
export type UndefinedToNull<T> = T extends undefined ? null : Required<T>; // exkuldiert undefined aus T

/**
 * Vergleichsfunktion für Array.sort(), die zwei Objekte vergleicht.
 * @returns -1, wenn a < b; 0, wenn a == b; 1, wenn a > b
 */
export type Comparator<T extends NonUndefinedValue> = (a: T, b: T) => number;

const noSort = () => 0;
const not =
    <T>(f: (x: T) => boolean) =>
    (x: T) =>
        !f(x);

/**
 * Vergleichsfunktion, die zwei Objekte nach ihrer natürlichen Ordnung (i.e.
 * Anwendung von ===, < und >) sortiert.
 * Das Verhalten bei gemischten Arrays ist allerdings nicht definiert.
 */
export function byNaturalOrder<T extends DefinedValue>(a: T, b: T): number {
    if (a === b) return 0;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
}

/**
 * Vergleichsfunktion, die zwei Objekte zuerst in Strings umwandelt und dann
 * nach ihrem Unicode-Wert sortiert. Entspricht der Sortierung von Array.sort()
 * ohne Vergleichsfunktion.
 */
export function byStringValue<T extends NonUndefinedValue>(a: T, b: T): number {
    return byNaturalOrder(String(a), String(b));
}

/**
 * Vergleichsfunktion, die Zahlenwerte sortiert.
 * NaN wird als größer als alle anderen Werte behandelt.
 * Positiv Infinity wird als größer als alle anderen Werte behandelt.
 * Negativ Infinity wird als kleiner als alle anderen Werte behandelt.
 */
export const byNumber: (a: number, b: number) => number = bisect(not(isInvalidNumber), (a: number, b: number) => a - b, noSort);

/**
 * Vergleichsfunktion für boolean Werte, ordnet false vor true an.
 */
export function byBoolean(a: boolean, b: boolean): number {
    return (b ? 1 : 0) - (a ? 1 : 0);
}

/**
 * Vergleichsfunktion, die zwei Strings nach de-AT Locale sortiert.
 * Leere Strings werden als kleiner als alle anderen Werte behandelt.
 */
export function byLocale(a: string, b: string): number {
    return a.localeCompare(b, 'de-AT');
}

/**
 * Sortiert Elemente, die der predicate-Funktion entsprechen, vor und Elemente, die nicht der predicate-Funktion entsprechen, nach.
 * Elemente der gleichen Gruppe werden untereinander nicht sortiert (i.e. Vergeichsfunktion gibt 0 zurück)
 *
 * @param predicate Funktion, die entscheidet, ob ein Element der vor- oder nachgereihten Gruppe angehört
 */
export function byPredicate<T extends NonUndefinedValue>(predicate: (o: T) => boolean): Comparator<T> {
    return (a: T, b: T) => {
        return byBoolean(predicate(a), predicate(b));
    };
}

/**
 * Vergleichsfunktion, die zwei Datumswerte vergleicht.
 * Invalid Dates werden als größer als alle anderen Werte behandelt.
 */
export const byDate: (a: Date, b: Date) => number = bisect(not(isInvalidDate), (a: Date, b: Date) => a.getTime() - b.getTime(), noSort);

const isType = (t: unknown) => (x: unknown) => (typeof t === 'string' && typeof x === t) || (typeof t === 'function' && x instanceof t);

/**
 * Vergleichsfunktion, die zur Laufzeit den Typ der zu vergleichenden Werte
 * erkennt und die passende Vergleichsfunktion anwendet. Fällt auf die Vergleichsfunktion
 * byStringValue zurück (entspricht dem Standardverhalten von Array.sort()), wenn
 * Datentypen ohne entsprechender Vergleichsfunktion verglichen werden.
 *
 * null Werte werden ans Ende sortiert.
 *
 * Bei gemischten Arrays werden die Werte zunächst nach ihrem Typ sortiert.
 */
export const byDetectType: <T extends NonUndefinedValue>(a: T, b: T) => number = multiSort(
    // noSort als bisect nonMatchingComparator um die nächste Vergleichsfunktion im multiSort anzuwenden
    bisect(not(isUndefined), noSort, noSort),
    bisect(not(isNull), noSort, noSort),
    bisect(isType('boolean'), byBoolean, noSort),
    bisect(isType('number'), byNumber, noSort),
    bisect(isType('bigint'), byNaturalOrder, noSort),
    bisect(isType('symbol'), byStringValue, noSort),
    bisect(isType('string'), byLocale, noSort),
    bisect(isType(Date), byDate, noSort),
    bisect(isType('object'), byStringValue, noSort),
    bisect(isType('function'), byStringValue, byStringValue),
);

/**
 * Erstelle eine Vergleichsfunktion, die zwei Objekte nach einem Property vergleicht.
 * Properties, die undefined sind, werden der Vergleichsfunktion als null übergeben.
 * @param transformer Funktion, die das Property aus dem Objekt extrahiert
 * @param comparator Vergleichsfunktion für das Property
 */
export function withProp<T extends NonUndefinedValue, P>(transformer: (o: T) => P, comparator?: Comparator<UndefinedToNull<P>>): Comparator<T>;

/**
 * Erstelle eine Vergleichsfunktion, die zwei Objekte nach einem Property vergleicht.
 * Properties, die undefined sind, werden der Vergleichsfunktion als null übergeben.
 * @param prop Name des Properties
 * @param comparator Vergleichsfunktion für das Property
 */
export function withProp<T extends NonUndefinedValue, K extends keyof NonNullable<T>>(prop: K, comparator?: Comparator<UndefinedToNull<NonNullable<T>[K]>>): Comparator<T>;
export function withProp<T extends NonUndefinedValue, P, K extends keyof T>(
    prop: K | ((o: T) => P),
    comparator: Comparator<UndefinedToNull<P | T[K]>> = byDetectType,
): Comparator<T> {
    if (typeof prop === 'string' || typeof prop === 'symbol') {
        const propName = prop as string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (a: T, b: T) => comparator((a as any)?.[prop] ?? null, (b as any)?.[propName] ?? null);
    }
    if (typeof prop === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (a: T, b: T) => comparator(prop(a) ?? (null as any), prop(b) ?? (null as any));
    }
    throw new Error('No property name or transformer function supplied to withProp');
}

/**
 * Erstelle eine Vergleichsfunktion, die zwei Objekte in einen Wert umwandelt, nach dem sortiert wird.
 * undefined Werte werde der Vergleichsfunktion als null übergeben.
 *
 * alias für withProp
 *
 * @param transformer Funktion, die aus dem Objekt einen Wert berechnet
 * @param comparator Vergleichsfunktion für den Wert
 */
export const withValue: <T extends NonUndefinedValue, P>(transformer: (o: T) => P, comparator?: Comparator<UndefinedToNull<P>>) => Comparator<T> = withProp;

/**
 * Erstellt eine Vergleichsfunktion, die mehrere Vergleichsfunktionen kombiniert.
 * Die Vergleichsfunktionen werden nacheinander aufgerufen, bis eine Funktion die
 * zwei zu vergleichenden Werte als ungleich erkennt (Rückgabewert ungleich 0).
 *
 * Die Vergleichsfunktionskette kann unterbrochen werden, indem eine Funktion
 * einen BreakMultiSort Error wirft.
 *
 * @param comparators die zu kombinierenden Vergleichsfunktionen
 */
export function multiSort<T extends NonUndefinedValue>(...comparators: Comparator<T>[]): Comparator<T> {
    if (!comparators.length) {
        return byDetectType;
    }
    return (a: T, b: T) => {
        for (const comparator of comparators) {
            const result = comparator(a, b);
            if (result !== 0) {
                return result;
            }
        }
        return 0;
    };
}

/**
 * Teilt den Datensatz in zwei Teile und ordnet sie hintereinander an.
 * Der erste Teil entspricht der predicate-Funktion und wird nach dem matchingComparator sortiert.
 * Der zweite Teil entspricht nicht der predicate-Funktion und wird nach dem nonMatchingComparator sortiert.
 *
 * @param predicate Funktion, die entscheidet, ob ein Element dem Kriterium entspricht
 * @param matchingComparator Vergleichsfunktion für alle Elemente, die dem Kriterium entsprechen
 * @param nonMatchingComparator Vergleichsfunktion für alle Elemente, die nicht dem Kriterium entsprechen
 */
export function bisect<T extends NonUndefinedValue, V extends T, U extends T>(
    predicate: (o: T) => boolean,
    matchingComparator: Comparator<V> = byDetectType,
    nonMatchingComparator: Comparator<U> = byDetectType,
): Comparator<T> {
    return (a: T, b: T) => {
        const aMatches = predicate(a);
        const bMatches = predicate(b);
        if (aMatches && bMatches) {
            return matchingComparator(a as V, b as V);
        } else if (aMatches) {
            return -1;
        } else if (bMatches) {
            return 1;
        }
        return nonMatchingComparator(a as U, b as U);
    };
}

const invalidOrder = [isUndefined, isNull, isInvalidNumber, isBlankString, isInvalidDate];

function getInvalidOrderRank(x: unknown): number {
    for (let i = 0; i < invalidOrder.length; i++) {
        if (invalidOrder[i](x)) {
            return i;
        }
    }
    return invalidOrder.length;
}

function byInvalid(a: unknown, b: unknown): number {
    const result = getInvalidOrderRank(a) - getInvalidOrderRank(b);
    if (result === 0) {
        if (typeof a === 'string' && typeof b === 'string') {
            return a.localeCompare(b);
        }
    }
    return result;
}

function isUndefined(x: unknown): boolean {
    return typeof x === 'undefined';
}

function isNull(x: unknown): boolean {
    return x === null;
}

function isInvalidNumber(x: unknown): boolean {
    return typeof x === 'number' && isNaN(x);
}

function isBlankString(x: unknown): boolean {
    return typeof x === 'string' && x.trim().length === 0;
}

/**
 * Bestimmt, was durch nilFirst und nilLast und orderNil als null-Wert interpretiert wird.
 * null, NaN und invalid Dates werden als null-Werte interpretiert.
 * (undefined wird nicht als null-Wert interpretiert, da es von Array.sort() gesondert behandelt wird.)
 */
function isNil(x: unknown): boolean {
    return x === null || isInvalidNumber(x) || isInvalidDate(x) || isBlankString(x);
}

/**
 * undefined Werte, die direkt im Array stehen, werden durch Array.sort() immer direkt and das Ende des Arrays sortiert.
 * Die Vergleichsfunktion wird dabei gar nicht erst mit diesen undefined Werten aufgerufen. Um dieses Verhalten im Code
 * sichtbar zu machen, muss diese Funktion angegeben werden, um undefined Werte zu behandeln, bevor die restlichen
 * Werte durch die Vergleichsfunktion verglichen werden.
 * @param comparator die eigentliche Vergleichsfunktion, die angewandt werden soll
 */
export function undefinedLast<T extends NonUndefinedValue>(comparator: Comparator<T>): (a: T | undefined, b: T | undefined) => number {
    // falls undefinedLast aber nicht als direkter Parameter in Array.sort() verwendet wird und tatsächlich undefined übergeben bekommt, sortieren wir diese aktiv ans Ende
    return reverse(bisect(isUndefined, noSort, comparator)) as (a: T | undefined, b: T | undefined) => number;
}

/**
 * Erstellt eine Vergleichsfunktion, die zunächst alle nil-Werte (null, NaN, invalid Dates, blank Strings)
 * vorreiht, bevor die eigentliche Vergleichsfunktion angewandt wird.
 * @param comparator die zugrunde liegende Vergleichsfunktion
 */
export function nilFirst<T extends NonUndefinedValue>(comparator: Comparator<T> = byDetectType): Comparator<T | null> {
    return orderNil('first', comparator);
}

/**
 * Erstellt eine Vergleichsfunktion, die zunächst alle nil-Werte (null, NaN, invalid Dates, blank Strings)
 * nachreiht, bevor die eigentliche Vergleichsfunktion angewandt wird.
 * @param comparator die zugrunde liegende Vergleichsfunktion
 */
export function nilLast<T extends NonUndefinedValue>(comparator: Comparator<T> = byDetectType): Comparator<T | null> {
    return orderNil('last', comparator);
}

/**
 * Erstellt eine Vergleichsfunktion, die zunächst alle nil-Werte (null, NaN, invalid Dates, blank Strings)
 * vor- oder nachreiht, bevor die eigentliche Vergleichsfunktion angewandt wird.
 * @param dir Platzierung der null-Werte, 'first' oder 'last'
 * @param comparator die zugrunde liegende Vergleichsfunktion
 */
export function orderNil<T extends NonUndefinedValue>(dir: 'first' | 'last', comparator: Comparator<T> = byDetectType): Comparator<T | null> {
    const orderDir = dir === 'first' ? 1 : -1;
    return order(orderDir, bisect(isNil, byInvalid, order(orderDir, comparator)));
}

/**
 * Dreht die Sortierrichtung einer Vergleichsfunktion um.
 * @param comparator die zu invertierende Vergleichsfunktion
 */
export function reverse<T extends NonUndefinedValue>(comparator: Comparator<T> = byDetectType): Comparator<T> {
    return order('desc', comparator);
}

/**
 * Belässt die Sortierrichtung einer Vergleichsfunktion oder dreht diese um.
 * @param dir Sortierrichtung, 'asc' oder 'desc' oder 1 oder -1
 * @param comparator die zu sortierende Vergleichsfunktion
 */
export function order<T extends NonUndefinedValue>(dir: 'asc' | 'desc' | number, comparator: Comparator<T> = byDetectType): Comparator<T> {
    if (typeof dir === 'number') {
        dir = dir < 0 ? 'desc' : 'asc';
    }
    return dir === 'asc' ? comparator : (a: T, b: T) => comparator(a, b) * -1;
}

/**
 * Liefert den größten Wert aus einer Liste zurück.
 * undefined Werte werden als null verglichen und ggf. zurückgegeben.
 * @param list Liste von Werten
 * @param comparator Vergleichsfunktion, zur Bestimmung des größten Wertes
 */
export function getMax<T>(list: T[], comparator: Comparator<UndefinedToNull<T>> = nilFirst(byDetectType)): UndefinedToNull<T> | undefined {
    if (list.length === 0) {
        return undefined;
    }
    return list.reduce((min, current) => (comparator(min ?? (null as any), current ?? (null as any)) >= 0 ? min : current)) ?? (null as any);
}

/**
 * Liefert den kleinsten Wert aus einer Liste zurück.
 * undefined Werte werden als null verglichen und ggf. zurückgegeben.
 * @param list Liste von Werten
 * @param comparator Vergleichsfunktion, zur Bestimmung des kleinsten Wertes
 */
export function getMin<T>(list: T[], comparator: Comparator<UndefinedToNull<T>> = nilLast(byDetectType)): UndefinedToNull<T> | undefined {
    if (list.length === 0) {
        return undefined;
    }
    return list.reduce((min, current) => (comparator(min ?? (null as any), current ?? (null as any)) <= 0 ? min : current)) ?? (null as any);
}
